import React, { ReactNode } from "react";
import I18n from "i18n";
import session from "models/sessionInstance";
import _ from 'underscore';
import { formatCurrency } from '../utils.js';

interface KeywordPosition {
  position: "title" | "caption" | "content";
  count: number;
}

interface Keyword {
  keyword: string;
  positions: KeywordPosition[];
}

interface Props {
  agentResult: any;
}

export default function DatalakeMetadataView({ agentResult }: Props) {
  const clipMetadata = agentResult.get("clip_metadata");
  const clipAttachmentCount = agentResult.get("clip_attachments_count");
  const locale = session.get("icu_locale");
  let articleAdPrice;
  if (
    _.has(clipMetadata, "article_ad_price") &&
    _.has(clipMetadata, "article_ad_currency") &&
    clipMetadata.article_ad_price !== null &&
    clipMetadata.article_ad_currency !== null
  ) {
    articleAdPrice = formatCurrency(
      clipMetadata.article_ad_price,
      clipMetadata.article_ad_currency,
      locale
    );
  }
  let diveId: ReactNode = "";
  if (
    (session.can("internal_access") || session.isImpersonated()) &&
    clipMetadata.data_lake_identity &&
    clipMetadata.dive_url
  ) {
    diveId = (
      <>
        Dive ID:{" "}
        <a
          href={clipMetadata.dive_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {clipMetadata.data_lake_identity}
        </a>
      </>
    );
  }

  // nonUserSpecificMetadata
  let clipsMetadata = {
    author: clipMetadata.author,
    pageHeading:
      clipMetadata.page_heading && `Ressort: ${clipMetadata.page_heading}`,
    page: clipMetadata.page,
    wordCount:
      clipMetadata.word_count &&
      `${clipMetadata.word_count} ${I18n.t(
        "webapp.datalake_results.result_details.words"
      )}`,
    imageCount:
      clipAttachmentCount !== 0
        ? `${clipAttachmentCount} ${I18n.t(
            "webapp.datalake_results.result_details.image_count"
          )}`
        : I18n.t("webapp.datalake_results.result_details.no_image"),
    articleAdPrice: articleAdPrice,
    diveId: diveId,
  };

  // userSpecificMetadata
  let keywords = agentResult.get("keywords");
  let formattedKeywords: Record<string, string> = {};

  if (keywords && keywords.length > 0) {
    const positionMap: Record<string, string> = {
      title: "headline",
      caption: "caption",
      content: "body text",
    };

    const translationKeyMap: Record<string, string> = {
      "headline": "webapp.datalake_results.result_details.position_headline",
      "caption": "webapp.datalake_results.result_details.position_caption",
      "body text": "webapp.datalake_results.result_details.position_body_text",
    };

    keywords.forEach((keywordObj: Keyword) => {
      const { keyword, positions } = keywordObj;
      const descriptions = positions.map((posObj) => {
        const mappedPosition = positionMap[posObj.position] || posObj.position;
        const translationKey = translationKeyMap[mappedPosition];
        const translatedPosition = translationKey
          ? I18n.t(translationKey)
          : mappedPosition;
        if (posObj.count > 1) {
          return `${translatedPosition} (${posObj.count})`;
        }
        return translatedPosition;
      });
      formattedKeywords[keyword] = descriptions.join(", ");
    });
  }

  let agentCount = clipMetadata.agent_count;
  let searchAgentTitle = "";

  if (agentCount > 1) {
    searchAgentTitle = I18n.t(
      "webapp.datalake_results.result_details.several_search_agent"
    );
  } else {
    searchAgentTitle = agentResult.agent.get("name");
  }

  const searchAgentMetadata = {
    searchAgentTitle: searchAgentTitle,
    formattedKeywords: formattedKeywords,
  };

  return (
    <div
      style={{ display: "flex", gap: "3em", justifyContent: "space-between" }}
    >
      <div className="col1of2">
        <h2
          className="meta__title"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          {I18n.t("webapp.datalake_results.result_details.article_title")}
          <span className="meta__body meta__value">{clipsMetadata.diveId}</span>
        </h2>
        <dl className="meta__body">
          <dt className="meta__label">
            {I18n.t("webapp.agents.results.result_details.metadata.author")}:&nbsp;
          </dt>
          <dd className="meta__value">{clipsMetadata.author}</dd>
          <dd className="meta__value">{clipsMetadata.pageHeading}</dd>
          <dt className="meta__label">
            {I18n.t("webapp.agents.results.result_details.metadata.page")}:&nbsp;
          </dt>
          <dd className="meta__value">{clipsMetadata.page}</dd>
          <dt className="meta__label">
            {I18n.t("webapp.agents.results.result_details.metadata.word_count")}
            :&nbsp;
          </dt>
          <dd className="meta__value">{clipsMetadata.wordCount}</dd>
          <dt className="meta__label">
            {I18n.t(
              "webapp.agents.results.result_details.metadata.image_count",
            )}
            :&nbsp;
          </dt>
          <dd className="meta__value">{clipsMetadata.imageCount}</dd>
          {clipsMetadata.articleAdPrice && (
            <>
              <dt className="meta__label">
                {I18n.t(
                  "webapp.agents.results.result_details.article_ad_price",
                )}
                :&nbsp;
              </dt>
              <dd className="meta__value">{clipsMetadata.articleAdPrice}</dd>
            </>
          )}
        </dl>
      </div>
      <div className="col1of2">
        <h2
          className="meta__title"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          {searchAgentMetadata.searchAgentTitle}
        </h2>
        <dl className="meta__body">
          {searchAgentMetadata.formattedKeywords &&
            Object.entries(searchAgentMetadata.formattedKeywords).map(
              ([keyword, description]) => (
                <dd className="meta__value" key={keyword}>
                  <strong>{keyword}</strong>: {description}
                </dd>
              ),
            )}
        </dl>
      </div>
    </div>
  );
}
